import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { ReactComponent as HomeLocationIcon } from "../../assets/images/home-location.svg";
import { ReactComponent as WorkLocationIcon } from "../../assets/images/work-location.svg";
import { ReactComponent as GrossSalaryIcon } from "../../assets/images/gross-salary.svg";
import { ReactComponent as AvgHousingCostIcon } from "../../assets/images/avg-housing-cost.svg";
import { ReactComponent as IncomeTaxIcon } from "../../assets/images/income-tax.svg";
import { ReactComponent as SavingsIcon } from "../../assets/images/savigs.svg";

import { components } from "react-select";
import { Row, Col, Card, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import dashboardData from "../../data/dashboardData";
import "./Comparison.scss";
import { useDispatch, useSelector } from "react-redux";
import { compare } from "../../redux/slices/comparison.Slice";
import {
  getRoles,
  getAllCities,
  getAllStates,
} from "../../redux/slices/authSlice";
import ActionModal from "./ActionModal";
import { useNavigate } from "react-router-dom";
import { allowedNames, allowedRolesForFree } from "../../constants";
import { useLocation } from "react-router-dom";
import ComparisonModal from "../restriction-modals/ComparisonModal";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import CompareDailyLimitModal from "../restriction-modals/CompareDailyLimitModal";
import {
  activityEnd,
  activityStart,
} from "../../redux/slices/trackActivitySlice";
import usePageTracking from "../../hooks/usePageTracking";
import { UserRound } from "lucide-react";
import {
  PROFILE_COMPLETION_FIELDS,
  getProfileCompletionPercent,
} from "../../utils/profileCompletion";
import useTour from "../../components/tour/useTour";
import GuidedTour from "../../components/tour/GuidedTour";
import getComparisonTourSteps from "./comparisonTourSteps";

const PROFILE_COMPLETION_FIELDS_BY_KEY = Object.fromEntries(
  PROFILE_COMPLETION_FIELDS.map((field) => [field.key, field]),
);

// Priority-ordered: the first of these still incomplete is the one shown,
// since it's the field most likely to affect the city comparison shown here.
const COMPARISON_RELEVANT_PROFILE_NUDGES = [
  {
    key: "role",
    message: "Add your nursing role to compare accurate salaries between cities.",
  },
  {
    key: "city",
    message: "Add your current city to see how it compares.",
  },
  {
    key: "salary",
    message: "Add your preferred salary to compare against these cities.",
  },
];

/* ── Info tooltip — small (i) icon. Self-contained (inline styles) so it
   doesn't depend on Comparison.scss classes. Desktop (real mouse) hovers
   in/out; touch devices tap-to-toggle + tap-outside-to-close (hover alone
   flickers open/closed on tap — mobile browsers synthesize a
   mouseenter+mouseleave pair before the click lands). ─────────────────── */
const isHoverCapable = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

// Tooltip box width — clamped further per-viewport in reposition() below.
const INFO_TIP_WIDTH = 220;
const INFO_TIP_MARGIN = 12;

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const hoverCapable = useRef(isHoverCapable()).current;

  // Anchors the popup to the icon's actual screen position (fixed, not
  // relative to the wrapper) and clamps it so it never spills past the
  // left/right edge of the viewport — the old centered-under-icon
  // placement clipped off-screen for icons near a narrow column's edge.
  const reposition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(INFO_TIP_WIDTH, vw - INFO_TIP_MARGIN * 2);
    const iconCenter = rect.left + rect.width / 2;
    const left = Math.max(
      INFO_TIP_MARGIN,
      Math.min(iconCenter - width / 2, vw - width - INFO_TIP_MARGIN),
    );
    const arrowLeft = Math.max(10, Math.min(iconCenter - left, width - 10));
    setPos({ left, bottom: window.innerHeight - rect.top + 8, width, arrowLeft });
  };

  useLayoutEffect(() => {
    if (!show) return undefined;
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [show]);

  return (
    <span
      ref={wrapRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 6,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={hoverCapable ? () => setShow(false) : undefined}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: 14, height: 14, color: "var(--mmp-text-secondary)", cursor: "pointer" }}
        aria-label="More info"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShow((s) => !s);
        }}
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-5" />
        <path d="M12 8h.01" />
      </svg>
      {show &&
        pos &&
        createPortal(
          // Portaled to document.body: .metric-card gets `transform:
          // translateY(-2px)` on :hover — active for the exact moment the
          // mouse is over the card to reach this icon — which makes the
          // card the containing block for a position:fixed descendant
          // instead of the viewport, so our getBoundingClientRect()-based
          // left/bottom values land in the wrong place. Rendering here
          // escapes that trap.
          <div
            role="tooltip"
            style={{
              position: "fixed",
              left: pos.left,
              bottom: pos.bottom,
              background: "#0f172a",
              color: "#fff",
              fontSize: 12,
              fontWeight: 400,
              textTransform: "none",
              letterSpacing: "normal",
              lineHeight: 1.4,
              padding: "8px 10px",
              borderRadius: 8,
              width: pos.width,
              zIndex: 2000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            }}
          >
            {text}
            <div
              style={{
                position: "absolute",
                bottom: -4,
                left: pos.arrowLeft,
                transform: "translateX(-50%) rotate(45deg)",
                width: 8,
                height: 8,
                background: "#0f172a",
              }}
            />
          </div>,
          document.body,
        )}
    </span>
  );
};

const Comparison = () => {
  // Guided tour — auto-starts on first visit, replayable from the header.
  const comparisonTourSteps = useMemo(() => getComparisonTourSteps(), []);
  const { isOpen: isTourOpen, finish: finishTour } = useTour("comparison");

  const fetchOnceRef = useRef(false);
  const countNextFetchRef = useRef(false);
  const navigate = useNavigate();
  const dataLoadedFromNav = useRef(false);
  const profilePrefilledRef = useRef(false);
  const dispatch = useDispatch();
  const store = useSelector((state) => state.auth);
  const [upgradeSource, setUpgradeSource] = useState(null);
  const compareStore = useSelector((state) => state.comparison);
  const profileStore = useSelector((state) => state.profile);

  // "Complete your profile" nudge shown below the title, in its own card —
  // picks the single highest-priority missing field rather than a generic
  // percentage, since that's more actionable here. Dismissal is in-memory
  // only (not persisted) so the nudge reappears on reload, re-login, or a
  // fresh tab — not just once ever.
  const [profileNudgeDismissed, setProfileNudgeDismissed] = useState(false);
  const comparisonProfileData = profileStore?.profileItem?.data;
  const profileCompletionPercent = getProfileCompletionPercent(
    comparisonProfileData,
  );
  const activeProfileNudge = comparisonProfileData
    ? COMPARISON_RELEVANT_PROFILE_NUDGES.find((nudge) => {
        const field = PROFILE_COMPLETION_FIELDS_BY_KEY[nudge.key];
        return field && !field.isComplete(comparisonProfileData);
      })
    : null;
  const dismissProfileNudge = () => {
    setProfileNudgeDismissed(true);
  };

  const [compareStates, setCompareStates] = useState({
    work: null,
    live: null,
  });
  // const [compareStates, setCompareStates] = useState({
  //   work: null,
  //   live: null,
  // });
  const [compareCities, setCompareCities] = useState({
    current: { state: null, city: null },
    destination: { state: null, city: null },
  });
  // const [compareCities, setCompareCities] = useState({
  //   current: { state: null, city: null },
  //   destination: { state: null, city: null },
  // });
  const authStore = useSelector((state) => state.auth);

  const [stateOptions, setStateOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [workCityOptions, setWorkCityOptions] = useState([]);
  const [liveCityOptions, setLiveCityOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const [subText, setSubText] = useState({
    header: "",
    desc: "",
    massage1: "",
    pluspricem: "",
    pluspricey: "",
    desc2: "",
    promassage: "",
    propricem: "",
    propricey: "",
  });
  useEffect(() => {
    setCompareCities();
  }, []);

  const [subAlertModalVisible, setSubAlertModalVisible] = useState(false);
  const [subAlertCompModalVisible, setSubAlertCompModalVisible] = useState(
    false,
  );
  const [compareDailyLimitVisible, setCompareDailyLimitVisible] = useState(
    false,
  );
  const [isRestrictedData, setIsRestrictedData] = useState({
    is_comparisan: true,
    comparisanMessage: "",
    header: "",
    messageBody1: "",
    messageBody2: "",
  });
  const location = useLocation();

  const receivedData = location.state;
  const remainingCount = compareStore?.comparisonItem?.remaining_count;

  // Track Activity
  usePageTracking("COMPARISON");

  useEffect(() => {
    const payload = {
      page_name: "COMPARISON",
      platform: "web",
    };

    dispatch(activityStart(payload));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        activityEnd({
          page_name: "COMPARISON",
        }),
      );
    };
  }, [dispatch]);

  // --- Handle Data from Navigation (Pre-fill) ---
  useEffect(() => {
    // 1. Check if navigation data exists and hasn't been processed yet
    if (receivedData && !dataLoadedFromNav.current) {
      // 2. Ensure Redux Reference data is ready
      const availableRoles = store?.roleItems?.describe_roles;
      const availableStates =
        store?.stateItems?.data?.states || store?.stateItems?.states;

      if (availableRoles?.length > 0 && availableStates?.length > 0) {
        dataLoadedFromNav.current = true; // Mark as processed

        const {
          NursingRole,
          WorkState,
          WorkCity,
          LiveState,
          LiveCity,
        } = receivedData;
        const findStateId = (incomingState) => {
          if (!incomingState) return null;
          const found = availableStates.find(
            (s) =>
              s.state === incomingState.value ||
              s.state_full_name === incomingState.label,
          );
          return found ? found.id : null;
        };

        // --- Helper: Find Role ID ---
        const findRoleId = (incomingRole) => {
          if (!incomingRole) return null;
          // Match by ID (value) or Name (label)
          const found = availableRoles.find(
            (r) => r.id === incomingRole.value || r.name === incomingRole.label,
          );
          return found ? found.id : null;
        };

        const matchedRoleId = findRoleId(NursingRole);
        const matchedWorkStateId = findStateId(WorkState);
        const matchedLiveStateId = findStateId(LiveState);

        // Fetch Work Cities
        if (matchedWorkStateId) {
          const stateCode =
            WorkState?.value ||
            availableStates.find((s) => s.id === matchedWorkStateId)?.state;
          dispatch(getAllCities({ search_state: stateCode })).then((res) => {
            const cities =
              res.payload?.data?.cities || res.payload?.cities || [];
            setWorkCityOptions(
              cities.map((c) => ({ value: c.city, label: c.city })),
            );
          });
        }

        // Fetch Live Cities
        if (matchedLiveStateId) {
          const stateCode =
            LiveState?.value ||
            availableStates.find((s) => s.id === matchedLiveStateId)?.state;
          dispatch(getAllCities({ search_state: stateCode })).then((res) => {
            const cities =
              res.payload?.data?.cities || res.payload?.cities || [];
            setLiveCityOptions(
              cities.map((c) => ({ value: c.city, label: c.city })),
            );
          });
        }

        setState((prev) => ({
          ...prev,
          nursingRole: matchedRoleId,
          workState: matchedWorkStateId,
          workCity: WorkCity?.value,
          liveState: matchedLiveStateId,
          liveCity: LiveCity?.value,
          isLoading: { ...prev.isLoading, workCities: true, liveCities: true },
        }));
      }
    }
  }, [receivedData, store?.roleItems, store?.stateItems, dispatch]);

  // Pre-fill from profile store (only when no navigation data is present)
  useEffect(() => {
    if (receivedData || profilePrefilledRef.current) return;

    const profileData = profileStore?.profileItem?.data;
    const availableRoles = store?.roleItems?.describe_roles;
    const availableStates =
      store?.stateItems?.data?.states || store?.stateItems?.states;

    if (!profileData || !availableRoles?.length || !availableStates?.length)
      return;

    const nursingRole = profileData.nursing_role;
    const workCity = profileData.work_city;
    const workStateCode = profileData.work_state_info?.state;
    const liveCity = profileData.city;
    const liveStateCode = profileData.living_state_info?.state;

    if (
      !nursingRole ||
      !workCity ||
      !workStateCode ||
      !liveCity ||
      !liveStateCode
    )
      return;

    profilePrefilledRef.current = true;

    const matchedRole = availableRoles.find((r) => r.id === nursingRole.id);
    const matchedWorkState = availableStates.find(
      (s) => s.state === workStateCode,
    );
    const matchedLiveState = availableStates.find(
      (s) => s.state === liveStateCode,
    );

    if (matchedWorkState) {
      dispatch(getAllCities({ search_state: workStateCode })).then((res) => {
        const cities = res.payload?.data?.cities || res.payload?.cities || [];
        setWorkCityOptions(
          cities.map((c) => ({ value: c.city, label: c.city })),
        );
      });
      setCompareStates((prev) => ({
        ...prev,
        work: {
          value: matchedWorkState.id,
          label: matchedWorkState.state_full_name,
          code: matchedWorkState.state,
        },
      }));
    }

    if (matchedLiveState) {
      dispatch(getAllCities({ search_state: liveStateCode })).then((res) => {
        const cities = res.payload?.data?.cities || res.payload?.cities || [];
        setLiveCityOptions(
          cities.map((c) => ({ value: c.city, label: c.city })),
        );
      });
      setCompareStates((prev) => ({
        ...prev,
        live: {
          value: matchedLiveState.id,
          label: matchedLiveState.state_full_name,
          code: matchedLiveState.state,
        },
      }));
    }

    setState((prev) => ({
      ...prev,
      nursingRole: matchedRole ? matchedRole.id : null,
      workState: matchedWorkState ? matchedWorkState.id : null,
      workCity: workCity,
      liveState: matchedLiveState ? matchedLiveState.id : null,
      liveCity: liveCity,
    }));
  }, [
    receivedData,
    profileStore?.profileItem?.data,
    store?.roleItems,
    store?.stateItems,
    dispatch,
  ]);

  useEffect(() => {
    const didFetchRef = fetchOnceRef.current;
    if (!didFetchRef) {
      fetchOnceRef.current = true;
      dispatch(getRoles());
      dispatch(getAllStates());
      dispatch(getAllCities());
    }
  }, [dispatch]);

  useEffect(() => {
    if (store?.stateItems?.data?.states) {
      const options = store?.stateItems?.data?.states?.map((state) => ({
        value: state.id,
        label: state.state_full_name,
        code: state.state,
      }));
      setStateOptions(options);
    }
    if (store?.cityItems?.data?.cities) {
      const options = store.cityItems.data.cities.map((cityObj, index) => ({
        value: cityObj.city,
        label: cityObj.city,
      }));
      setCityOptions(options);
    }
  }, [store?.stateItems, store?.cityItems]);

  useEffect(() => {
    if (!store?.roleItems) return;

    // const currentPlan = profileStore?.profileItem?.data?.plan?.name;
    const currentPlan = authStore?.user?.plan?.name
      ? authStore?.user?.plan?.name
      : store?.profileItem?.data?.plan?.name;

    const data = store.roleItems.describe_roles.map((s) => {
      const roleName = s.name;

      let isDisabled = false;
      let requiredPlan = "";

      if (currentPlan === "Free") {
        if (!allowedRolesForFree.includes(roleName)) {
          isDisabled = true;
          requiredPlan = "Pro";
        }
      }

      return {
        value: s.id,
        label: roleName,
        isDisabled,
        requiredPlan,
      };
    });

    setRoleOptions(data);
  }, [profileStore, store, store?.profileItem, authStore?.user?.plan?.name]);

  const [state, setState] = useState({
    // User Selections
    nursingRole: null,
    workState: null,
    workCity: null,
    liveState: null,
    liveCity: null,

    // Reference Data
    roles: [],
    states: [],
    workCities: [],
    liveCities: [],

    // Calculated Data
    workData: {
      salary: null,
      salaryRange: null,
      jobMarket: null,
    },
    liveData: {
      housing: null,
      utilities: null,
      transportation: null,
      taxes: null,
      otherCosts: null,
    },

    // Final Calculation
    takeHome: null,

    // UI State
    isLoading: {
      roles: false,
      workCities: false,
      liveCities: false,
      workData: false,
      liveData: false,
      calculation: false,
    },
    errors: {},
  });

  useLayoutEffect(() => {
    if (
      compareStore?.comparisonItem ||
      compareStore?.actionFlag === "COMP_SCS"
    ) {
      setIsRestrictedData({
        is_comparisan: compareStore?.comparisonItem.is_comparisan,
        comparisanMessage: compareStore?.comparisonItem.comparisanMessage,
        header: compareStore?.comparisonItem.header,
        messageBody1: compareStore?.comparisonItem.messageBody1,
        messageBody2: compareStore?.comparisonItem.messageBody2,
      });
    }
  }, [compareStore?.comparisonItem, compareStore?.actionFlag]);

  // useEffect(() => {
  //   if (
  //     compareStore?.actionFlag === "COMP_SCS" &&
  //     compareStore?.comparisonItem?.remaining_count === 0
  //   ) {
  //     setSubAlertModalVisible(true);
  //   }
  // }, [compareStore?.actionFlag, compareStore?.comparisonItem?.remaining_count]);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const remaining = compareStore?.comparisonItem?.remaining_count ?? 0;
    if (
      remaining === 0 &&
      compareStore?.comparisonItem?.is_comparisan === false
    ) {
      setUpgradeSource("comparison_limit");
      setCompareDailyLimitVisible(true);
    }
  }, [compareStore?.comparisonItem]);

  useEffect(() => {
    if (
      state.nursingRole &&
      state.workState &&
      state.workCity &&
      state.liveState &&
      state.liveCity
    ) {
      const getStateCode = (idOrValue) => {
        if (idOrValue === null || idOrValue === undefined || idOrValue === "")
          return null;
        const states =
          store?.stateItems?.data?.states || store?.stateItems?.states || [];
        const found = states.find(
          (s) =>
            s.id === Number(idOrValue) ||
            s.id === idOrValue ||
            s.state === idOrValue ||
            s.state_full_name === idOrValue,
        );
        if (found) return found.state;
        if (typeof idOrValue === "string" && idOrValue.trim() !== "")
          return idOrValue;
        return null;
      };

      const getRoleValue = (roleValue) => {
        if (roleValue === null || roleValue === undefined || roleValue === "")
          return null;
        const roles =
          store?.roleItems?.data?.describe_roles ||
          store?.roleItems?.describe_roles ||
          [];
        const found = roles.find(
          (r) =>
            r.id === Number(roleValue) ||
            r.id === roleValue ||
            r.name === roleValue ||
            r.slug === roleValue,
        );
        return found ? Number(found.id) : null;
      };

      const counted = countNextFetchRef.current;
      countNextFetchRef.current = false;
      const payload = {
        work_city: state.workCity || null,
        work_state: getStateCode(state.workState),
        living_city: state.liveCity || null,
        living_state: getStateCode(state.liveState),
        role: getRoleValue(state.nursingRole),
        is_count: counted,
      };

      // Dispatch and handle response
      dispatch(compare(payload));
      // .then((result) => {
      //   const comparisonItem = result?.payload?.comparisonItem;

      //   if (comparisonItem) {
      //     setIsRestrictedData({
      //       is_comparisan: comparisonItem.is_comparisan,
      //       comparisanMessage: comparisonItem.comparisanMessage,
      //       header: comparisonItem.header,
      //       messageBody1: comparisonItem.messageBody1,
      //       messageBody2: comparisonItem.messageBody2,
      //     });

      //     if (comparisonItem.remaining_count === 0) {
      //       setTimeout(() => {
      //         setSubAlertModalVisible(true);
      //       }, 300);
      //     }
      //   }
      // });
    }
  }, [
    state.nursingRole,
    state.workState,
    state.workCity,
    state.liveState,
    state.liveCity,
    dispatch,
  ]);

  // const workStateName = useMemo(() => {
  //   const states = store?.stateItems?.data?.states || [];
  //   const found = states.find((s) => s.id === Number(state.workState));
  //   return found ? found.state : state.workState || "—";
  // }, [store?.stateItems?.data?.states, state.workState]);

  // const liveStateName = useMemo(() => {
  //   const states = store?.stateItems?.data?.states || [];
  //   const found = states.find((s) => s.id === Number(state.liveState));
  //   return found ? found.state : state.liveState || "—";
  // }, [store?.stateItems?.data?.states, state.liveState]);

  const [cache] = useState(new Map());

  const loadReferenceData = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isLoading: { ...prev.isLoading, roles: true },
    }));

    try {
      // Extract roles and states from dashboardData
      const roles = Array.from(new Set(dashboardData.map((c) => c.role)));
      const states = Array.from(new Set(dashboardData.map((c) => c.state)));

      setState((prev) => ({
        ...prev,
        roles: roles.map((r) => ({ value: r, label: r })),
        states: states.map((s) => ({ value: s, label: s })),
        isLoading: { ...prev.isLoading, roles: false },
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, roles: "Failed to load nursing roles" },
        isLoading: { ...prev.isLoading, roles: false },
      }));
    }
  }, []);
  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);
  // Sequential Loading: When Nursing Role Selected

  // Pre-load salary ranges for selected role
  const preloadSalaryRanges = useCallback(
    (role) => {
      const cacheKey = `salary-ranges-${role}`;
      if (cache.has(cacheKey)) return;

      try {
        // Filter data for selected role
        const roleData = dashboardData.filter((d) => d.role === role);
        const salaryRanges = roleData.map((d) => ({
          city: d.city,
          state: d.state,
          salary: d.kpis[0].value,
          salaryMonthly: d.kpis[0].sub,
        }));

        cache.set(cacheKey, salaryRanges);
      } catch (error) {
        console.error("Failed to preload salary ranges:", error);
      }
    },
    [cache],
  );

  const handleRoleSelection = useCallback(
    (selectedOption) => {
      const role = selectedOption?.value || null;
      countNextFetchRef.current = true;

      setState((prev) => ({
        ...prev,
        nursingRole: role,
        workState: null,
        workCity: null,
        liveState: null,
        liveCity: null,
        workCities: [],
        liveCities: [],
        workData: { salary: null, salaryRange: null, jobMarket: null },
        liveData: {
          housing: null,
          utilities: null,
          transportation: null,
          taxes: null,
          otherCosts: null,
        },
        takeHome: null,
      }));

      if (role) {
        // Pre-load salary ranges for all cities (background loading)
        preloadSalaryRanges(role);
      }
    },
    [preloadSalaryRanges],
  );

  // When Work State Selected
  const handleWorkStateSelection = (selectedOption) => {
    // if (remainingCount === 0) {
    //   console.log("In data");
    //   setSubAlertModalVisible(true);
    //   return;
    // }
    countNextFetchRef.current = true;
    setCompareStates((prev) => ({ ...prev, work: selectedOption }));
    setState((prev) => ({
      ...prev,
      workState: selectedOption?.value || null,
      workCity: null,
      workData: { salary: null, salaryRange: null, jobMarket: null },
      takeHome: null,
      isLoading: { ...prev.isLoading, workCities: true },
    }));

    if (selectedOption) {
      dispatch(
        getAllCities({
          search_state: selectedOption.code,
          role: state.nursingRole || "",
        }),
      )
        .then((action) => {
          if (action.payload) {
            const cities =
              action.payload.data?.cities || action.payload.cities || [];
            setWorkCityOptions(
              cities.map((cityObj) => ({
                value: cityObj.city,
                label: cityObj.city,
              })),
            );
            setState((prev) => ({
              ...prev,
              isLoading: { ...prev.isLoading, workCities: false },
            }));
          }
        })
        .catch(() => {
          setState((prev) => ({
            ...prev,
            isLoading: { ...prev.isLoading, workCities: false },
          }));
        });
    } else {
      setWorkCityOptions([]);
      setState((prev) => ({
        ...prev,
        isLoading: { ...prev.isLoading, workCities: false },
      }));
    }
  };

  // const loadWorkCities = useCallback((workState) => {
  //   try {
  //     // Get cities for selected state
  //     const cities = Array.from(
  //       new Set(
  //         dashboardData
  //           .filter((d) => d.state === workState?.label)
  //           .map((d) => d.city)
  //       )
  //     );

  //     setState((prev) => ({
  //       ...prev,
  //       workCities: cities.map((c) => ({ value: c, label: c })),
  //       isLoading: { ...prev.isLoading, workCities: false },
  //     }));
  //   } catch (error) {
  //     setState((prev) => ({
  //       ...prev,
  //       errors: { ...prev.errors, workCities: "Failed to load cities" },
  //       isLoading: { ...prev.isLoading, workCities: false },
  //     }));
  //   }
  // }, []);

  // When Work City Selected
  const handleWorkCitySelection = (selectedOption) => {
    // if (remainingCount === 0) {
    //   console.log("In data");
    //   setSubAlertModalVisible(true);
    //   return;
    // }
    countNextFetchRef.current = true;
    const workCity = selectedOption?.value || null;
    setState((prev) => ({
      ...prev,
      workCity,
      workData: { salary: null, salaryRange: null, jobMarket: null },
      takeHome: null,
      isLoading: { ...prev.isLoading, workData: true },
    }));

    if (workCity && state.workState && state.nursingRole) {
      loadWorkData(state.nursingRole, workCity, state.workState);
    }
  };

  const loadWorkData = useCallback((role, city, workState) => {
    try {
      // Find specific salary data
      const workLocationData = dashboardData.find(
        (d) => d.role === role && d.city === city && d.state === workState,
      );

      if (workLocationData) {
        const salaryYearly = parseFloat(
          workLocationData.kpis[0].value.replace(/[$,/yr]/g, ""),
        );
        const salaryMonthly = salaryYearly / 12;

        setState((prev) => ({
          ...prev,
          workData: {
            salary: salaryMonthly,
            salaryRange: {
              yearly: salaryYearly,
              monthly: salaryMonthly,
              formatted:
                workLocationData.kpis[0].sub ||
                `$${Math.round(salaryMonthly).toLocaleString()}/month`,
            },
            jobMarket: {
              hospitals: workLocationData.hospitals,
              crime: workLocationData.crime,
            },
          },
          isLoading: { ...prev.isLoading, workData: false },
        }));

        // Trigger calculation if live data is available
        // if (state.liveData.housing) {
        //   calculateTakeHome();
        // }
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, workData: "Failed to load salary data" },
        isLoading: { ...prev.isLoading, workData: false },
      }));
    }
  }, []);

  // When Live State Selected
  const handleLiveStateSelection = (selectedOption) => {
    // console.log("Outer Value");
    // if (remainingCount === 0) {
    //   console.log("In data");
    //   setSubAlertModalVisible(true);
    //   return;
    // }
    countNextFetchRef.current = true;
    setCompareStates((prev) => ({ ...prev, live: selectedOption }));
    setState((prev) => ({
      ...prev,
      liveState: selectedOption?.value || null,
      liveCity: null,
      liveData: {
        housing: null,
        utilities: null,
        transportation: null,
        taxes: null,
        otherCosts: null,
      },
      takeHome: null,
      isLoading: { ...prev.isLoading, liveCities: true },
    }));

    if (selectedOption) {
      dispatch(
        getAllCities({
          search_state: selectedOption.code,
          role: state.nursingRole || "",
        }),
      )
        .then((action) => {
          if (action.payload) {
            const cities =
              action.payload.data?.cities || action.payload.cities || [];
            setLiveCityOptions(
              cities.map((cityObj) => ({
                value: cityObj.city,
                label: cityObj.city,
              })),
            );
            setState((prev) => ({
              ...prev,
              isLoading: { ...prev.isLoading, liveCities: false },
            }));
          }
        })
        .catch(() => {
          setState((prev) => ({
            ...prev,
            isLoading: { ...prev.isLoading, liveCities: false },
          }));
        });
    } else {
      setLiveCityOptions([]);
      setState((prev) => ({
        ...prev,
        isLoading: { ...prev.isLoading, liveCities: false },
      }));
    }
  };

  // const loadLiveCities = useCallback((liveState) => {
  //   try {
  //     // Get cities for selected state
  //     const cities = Array.from(
  //       new Set(
  //         dashboardData
  //           .filter((d) => d.state === liveState.label)
  //           .map((d) => d.city)
  //       )
  //     );

  //     setState((prev) => ({
  //       ...prev,
  //       liveCities: cities.map((c) => ({ value: c, label: c })),
  //       isLoading: { ...prev.isLoading, liveCities: false },
  //     }));
  //   } catch (error) {
  //     setState((prev) => ({
  //       ...prev,
  //       errors: { ...prev.errors, liveCities: "Failed to load cities" },
  //       isLoading: { ...prev.isLoading, liveCities: false },
  //     }));
  //   }
  // }, []);

  // When Live City Selected
  const handleLiveCitySelection = (selectedOption) => {
    const liveCity = selectedOption?.value || null;
    // if (remainingCount === 0) {
    //   console.log("In data");
    //   setSubAlertModalVisible(true);
    //   return;
    // }
    countNextFetchRef.current = true;
    setState((prev) => ({
      ...prev,
      liveCity,
      liveData: {
        housing: null,
        utilities: null,
        transportation: null,
        taxes: null,
        otherCosts: null,
      },
      takeHome: null,
      isLoading: { ...prev.isLoading, liveData: true },
    }));

    if (liveCity && state.liveState) {
      loadLiveData(liveCity, state.liveState);
    }
  };

  const loadLiveData = useCallback((city, liveState) => {
    try {
      // Find cost of living data
      const liveLocationData = dashboardData.find(
        (d) => d.city === city && d.state === liveState,
      );

      if (liveLocationData) {
        const housingCost = parseFloat(
          liveLocationData.kpis[2].value.replace(/[$,/mo]/g, ""),
        );

        // Calculate estimated taxes and other expenses
        const estimatedTaxes = Math.round(housingCost * 0.15); // 15% of housing for taxes
        const estimatedUtilities = Math.round(housingCost * 0.12); // 12% of housing for utilities
        const estimatedTransportation = Math.round(housingCost * 0.08); // 8% of housing for transportation
        const otherCosts =
          estimatedTaxes + estimatedUtilities + estimatedTransportation;

        setState((prev) => ({
          ...prev,
          liveData: {
            housing: housingCost,
            utilities: estimatedUtilities,
            transportation: estimatedTransportation,
            taxes: estimatedTaxes,
            otherCosts: otherCosts,
          },
          isLoading: { ...prev.isLoading, liveData: false },
        }));

        // Trigger calculation if work data is available
        // if (state.workData.salary) {
        //   calculateTakeHome();
        // }
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          liveData: "Failed to load cost of living data",
        },
        isLoading: { ...prev.isLoading, liveData: false },
      }));
    }
  }, []);

  // Real-time Calculation
  // const calculateTakeHome = useCallback(() => {
  //   if (!state.workData.salary || !state.liveData.housing) return;

  //   setState((prev) => ({
  //     ...prev,
  //     isLoading: { ...prev.isLoading, calculation: true },
  //   }));

  //   try {
  //     const grossSalary = state.workData.salary;
  //     const housing = state.liveData.housing;
  //     const taxes = state.liveData.taxes;
  //     const otherExpenses =
  //       state.liveData.utilities + state.liveData.transportation;

  //     const takeHome = grossSalary - housing - taxes - otherExpenses;

  //     setState((prev) => ({
  //       ...prev,
  //       takeHome: Math.round(takeHome),
  //       isLoading: { ...prev.isLoading, calculation: false },
  //     }));
  //   } catch (error) {
  //     setState((prev) => ({
  //       ...prev,
  //       errors: {
  //         ...prev.errors,
  //         calculation: "Failed to calculate take-home pay",
  //       },
  //       isLoading: { ...prev.isLoading, calculation: false },
  //     }));
  //   }
  // }, [
  //   state.workData.salary,
  //   state.liveData.housing,
  //   state.liveData.taxes,
  //   state.liveData.utilities,
  //   state.liveData.transportation,
  // ]);

  // Trigger calculation when work or live data changes
  // useEffect(() => {
  //   if (state.workData.salary && state.liveData.housing) {
  //     calculateTakeHome();
  //   }
  // }, [state.workData.salary, state.liveData.housing, calculateTakeHome]);

  // Debug logs for development
  const CustomRoleOption = (props) => {
    const { isDisabled, data } = props;
    const { requiredPlan } = data;

    return (
      <div
        onClick={(e) => {
          if (isDisabled) {
            e.stopPropagation();
            const source = "nursing_role_limit";
            setUpgradeSource(source);
            if (requiredPlan === "Pro") {
              setSubText({
                header: "Subscription Required!",
                desc: "",
                massage1: "",
                massage2: "",
                desc2: "You need a Pro plan to unlock this role",
                promassage:
                  "Pro users see all 9 nursing roles, including NP, CRNA, and Midwife",
                // promassage: "Upgrade to Pro for full role visibility",
                propricem: "$2.99/month",
                propricey: "$29.99/year",
                plan: "Pro",
                lockedItemName: data.label,
              });
            }

            setSubAlertCompModalVisible(true);
          }
        }}
        style={{
          width: "100%",
          cursor: isDisabled ? "pointer" : "default",
        }}
      >
        <components.Option {...props}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: isDisabled ? "var(--mmp-text-secondary)" : "var(--mmp-text-primary)",
              }}
            >
              {data.label}
            </span>
            {data.requiredPlan && (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: "9px",
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "var(--mmp-accent)",
                  background: "var(--mmp-accent-bg)",
                  border: "1px solid var(--mmp-accent-border)",
                  borderRadius: "999px",
                  padding: "1px 6px",
                }}
              >
                {data.requiredPlan}
              </span>
            )}
          </div>
        </components.Option>
      </div>
    );
  };

  // const CustomRoleOption = (props) => {
  //   const { isDisabled } = props;

  //   return (
  //     <div
  //       onClick={(e) => {
  //         if (isDisabled) {
  //           e.stopPropagation(); // Prevents selection
  //           setSubText({
  //             header: "Subscription Required!",
  //             desc: "Plus or Pro plan is required",
  //           });
  //           setSubAlertModalVisible(true);
  //         }
  //         // For enabled options: do nothing → react-select handles selection normally
  //       }}
  //       style={{
  //         width: "100%",
  //         cursor: isDisabled ? "pointer" : "default",
  //       }}
  //     >
  //       <components.Option {...props} />
  //     </div>
  //   );
  // };

  return (
    <>
      <NoIndexSEO title={seoConfig.comparison.title} />
      <div className="comparison-content comparisonpage">
        <div className="app-page-title">
          <div className="page-title-heading">
            <h3 className="page-title">Comparison</h3>
          </div>
        </div>

        {activeProfileNudge && !profileNudgeDismissed && (
          <div
            className="comparison-profile-nudge"
            role="button"
            tabIndex={0}
            onClick={() => navigate("/profile")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate("/profile");
              }
            }}
          >
            <div className="comparison-profile-nudge-icon">
              <UserRound />
            </div>
            <div className="comparison-profile-nudge-body">
              <div className="comparison-profile-nudge-head">
                <span className="comparison-profile-nudge-eyebrow">
                  Complete your profile
                </span>
                <span className="comparison-profile-nudge-pct">
                  {profileCompletionPercent}%
                </span>
              </div>
              <div className="comparison-profile-nudge-bar-track">
                <div
                  className="comparison-profile-nudge-bar-fill"
                  style={{ width: `${profileCompletionPercent}%` }}
                />
              </div>
              <span className="comparison-profile-nudge-text">
                {activeProfileNudge.message}
              </span>
            </div>
            <button
              type="button"
              className="comparison-profile-nudge-close"
              aria-label="Dismiss"
              onClick={(e) => {
                e.stopPropagation();
                dismissProfileNudge();
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Error Display */}
        {/* {Object.keys(state.errors).length > 0 && (
        <Alert variant="danger" className="mb-4">
          {Object.values(state.errors).map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )} */}

        {/* Live here, Work there Section */}
        <Card className="comparison-page-box" data-tour="cmp-inputcard">
          <Card.Body>
            <div className="comparison-header-section">
              <h2 className="comparison-main-title">
                Live Here, Work There
                <InfoTooltip text="Compares pay, cost of living, and savings between the city you work in and the city you live in." />
              </h2>
              <p className="comparison-subtitle">
                Compare salary, cost of living, and savings between two
                locations.
              </p>
            </div>

            {/* Location Headers Row */}
            <Row className="location-headers-row mb-md-3">
              <Col lg={6}>
                <div className="location-header-inline" data-tour="cmp-work">
                  <div className="location-icon-inline">
                    <div className="icon">
                      <WorkLocationIcon />
                    </div>
                  </div>
                  <div>
                    <h5 className="location-title-inline">Work Location</h5>
                    <p className="location-description-inline">
                      Where your job is located
                    </p>
                  </div>
                </div>
              </Col>
              <Col lg={6}>
                <div className="location-header-inline" data-tour="cmp-live">
                  <div className="location-icon-inline">
                    <div className="icon">
                      <HomeLocationIcon />
                    </div>
                  </div>
                  <div>
                    <h5 className="location-title-inline">Living Location</h5>
                    <p className="location-description-inline">
                      Where you live
                    </p>
                  </div>
                </div>
              </Col>
            </Row>

            {/* All Dropdowns in Single Row */}
            <Row className="dropdowns-row g-3">
              {/* Nursing Role */}
              <Col lg={2} md={4} sm={6}>
                <Form.Group className="form-group" data-tour="cmp-role">
                  <Form.Label className="col-label">
                    Nursing Role
                    {state.isLoading?.roles && (
                      <Spinner size="sm" className="ms-2" />
                    )}
                  </Form.Label>
                  <Select
                    aria-label="Nursing Role"
                    value={
                      (
                        store?.roleItems?.describe_roles?.map((role) => ({
                          value: role.id,
                          label: role.name,
                        })) || []
                      ).find((option) => option.value === state.nursingRole) ||
                      null
                    }
                    onChange={(selectedOption) => {
                      handleRoleSelection(selectedOption);
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      // Menu + option styling kept in lockstep with the Paycheck
                      // Calculator dropdowns so all calculators feel identical.
                      menu: (base) => ({
                        ...base,
                        background: "var(--mmp-surface)",
                        borderRadius: 10,
                        marginTop: 4,
                        padding: 4,
                        boxShadow:
                          "0 8px 20px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.05)",
                        zIndex: 1000,
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      menuList: (base) => ({
                        ...base,
                        padding: 0,
                        maxHeight: 240,
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: 13,
                        fontWeight: state.isSelected ? 700 : 500,
                        borderRadius: 6,
                        padding: "7px 10px",
                        margin: 0,
                        cursor: state.isDisabled ? "pointer" : "default",
                        color: state.isSelected
                          ? "#fff"
                          : state.isDisabled
                          ? "var(--mmp-text-secondary)"
                          : "var(--mmp-text-primary)",
                        background: state.isSelected
                          ? "var(--mmp-accent)"
                          : state.isFocused
                          ? "var(--mmp-accent-bg)"
                          : "transparent",
                        opacity: 1,
                        overflow: "hidden",
                      }),
                    }}
                    options={roleOptions}
                    components={{ Option: CustomRoleOption }}
                    isOptionDisabled={(option) => option.isDisabled}
                    isClearable
                    placeholder="Select Role"
                    isLoading={state.isLoading?.roles}
                    isDisabled={state.isLoading?.roles}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>

              {/* Work State */}
              <Col lg={2} md={4} sm={6}>
                <Form.Group className="form-group" data-tour="cmp-workstate">
                  <Form.Label className="col-label">Work State</Form.Label>
                  <Select
                    aria-label="Work State"
                    value={
                      stateOptions.length > 0
                        ? stateOptions.find(
                            (option) => option.value === state.workState,
                          ) || null
                        : store?.stateItems?.states
                            ?.map((stateItem) => ({
                              value: stateItem.id,
                              label: stateItem.state_full_name,
                              code: stateItem.state,
                            }))
                            .find(
                              (option) => option.value === state.workState,
                            ) || null
                    }
                    onChange={handleWorkStateSelection}
                    menuPortalTarget={document.body}
                    options={
                      stateOptions.length > 0
                        ? stateOptions
                        : [
                            { value: "", label: "Select State" },
                            ...(store?.stateItems?.states?.map((stateItem) => ({
                              value: stateItem.id,
                              label: stateItem.state_full_name,
                              code: stateItem.state,
                            })) || []),
                          ]
                    }
                    styles={{
                      // Menu + option styling kept in lockstep with the Paycheck
                      // Calculator dropdowns so all calculators feel identical.
                      menu: (base) => ({
                        ...base,
                        background: "var(--mmp-surface)",
                        borderRadius: 10,
                        marginTop: 4,
                        padding: 4,
                        boxShadow:
                          "0 8px 20px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.05)",
                        zIndex: 1000,
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      menuList: (base) => ({
                        ...base,
                        padding: 0,
                        maxHeight: 240,
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: 13,
                        fontWeight: state.isSelected ? 700 : 500,
                        borderRadius: 6,
                        padding: "7px 10px",
                        margin: 0,
                        cursor: "pointer",
                        color: state.isSelected ? "#fff" : "var(--mmp-text-primary)",
                        background: state.isSelected
                          ? "var(--mmp-accent)"
                          : state.isFocused
                          ? "var(--mmp-accent-bg)"
                          : "transparent",
                        // Truncate long option labels with ellipsis so they
                        // can't push the menu wider than the trigger.
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }),
                    }}
                    isClearable
                    placeholder="Select State"
                    isDisabled={!state.nursingRole}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>

              {/* Work City */}
              <Col lg={2} md={4} sm={6}>
                <Form.Group className="form-group" data-tour="cmp-workcity">
                  <Form.Label className="col-label">Work City</Form.Label>
                  <Select
                    aria-label="Work City"
                    value={
                      workCityOptions.find(
                        (option) => option.value === state.workCity,
                      ) || null
                    }
                    onChange={handleWorkCitySelection}
                    menuPortalTarget={document.body}
                    options={[
                      { value: "", label: "Select City" },
                      ...workCityOptions,
                    ]}
                    styles={{
                      // Menu + option styling kept in lockstep with the Paycheck
                      // Calculator dropdowns so all calculators feel identical.
                      menu: (base) => ({
                        ...base,
                        background: "var(--mmp-surface)",
                        borderRadius: 10,
                        marginTop: 4,
                        padding: 4,
                        boxShadow:
                          "0 8px 20px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.05)",
                        zIndex: 1000,
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      menuList: (base) => ({
                        ...base,
                        padding: 0,
                        maxHeight: 240,
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: 13,
                        fontWeight: state.isSelected ? 700 : 500,
                        borderRadius: 6,
                        padding: "7px 10px",
                        margin: 0,
                        cursor: "pointer",
                        color: state.isSelected ? "#fff" : "var(--mmp-text-primary)",
                        background: state.isSelected
                          ? "var(--mmp-accent)"
                          : state.isFocused
                          ? "var(--mmp-accent-bg)"
                          : "transparent",
                        // Truncate long option labels with ellipsis so they
                        // can't push the menu wider than the trigger.
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }),
                    }}
                    isClearable
                    placeholder="Select City"
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>

              {/* Living Location Header - Only visible on mobile, separate from dropdowns */}
              <Col xs={12} className="living-location-header-wrapper">
                <div className="living-location-header-mobile">
                  <div className="location-header-inline" data-tour="cmp-live">
                    <div className="location-icon-inline">
                      <div className="icon">
                        <HomeLocationIcon />
                      </div>
                    </div>
                    <div>
                      <h5 className="location-title-inline">Living Location</h5>
                      <p className="location-description-inline">
                        Where you live
                      </p>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Live State */}
              <Col lg={2} md={4} sm={6}>
                <Form.Group className="form-group" data-tour="cmp-livestate">
                  <Form.Label className="col-label">Live State</Form.Label>
                  <Select
                    aria-label="Live State"
                    value={
                      stateOptions.length > 0
                        ? stateOptions.find(
                            (option) => option.value === state.liveState,
                          ) || null
                        : store?.stateItems?.states
                            ?.map((stateItem) => ({
                              value: stateItem.id,
                              label: stateItem.state_full_name,
                              code: stateItem.state,
                            }))
                            .find(
                              (option) => option.value === state.liveState,
                            ) || null
                    }
                    onChange={handleLiveStateSelection}
                    menuPortalTarget={document.body}
                    options={
                      stateOptions.length > 0
                        ? stateOptions
                        : [
                            { value: "", label: "Select State" },
                            ...(store?.stateItems?.states?.map((stateItem) => ({
                              value: stateItem.id,
                              label: stateItem.state_full_name,
                              code: stateItem.state,
                            })) || []),
                          ]
                    }
                    styles={{
                      // Menu + option styling kept in lockstep with the Paycheck
                      // Calculator dropdowns so all calculators feel identical.
                      menu: (base) => ({
                        ...base,
                        background: "var(--mmp-surface)",
                        borderRadius: 10,
                        marginTop: 4,
                        padding: 4,
                        boxShadow:
                          "0 8px 20px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.05)",
                        zIndex: 1000,
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      menuList: (base) => ({
                        ...base,
                        padding: 0,
                        maxHeight: 240,
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: 13,
                        fontWeight: state.isSelected ? 700 : 500,
                        borderRadius: 6,
                        padding: "7px 10px",
                        margin: 0,
                        cursor: "pointer",
                        color: state.isSelected ? "#fff" : "var(--mmp-text-primary)",
                        background: state.isSelected
                          ? "var(--mmp-accent)"
                          : state.isFocused
                          ? "var(--mmp-accent-bg)"
                          : "transparent",
                        // Truncate long option labels with ellipsis so they
                        // can't push the menu wider than the trigger.
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }),
                    }}
                    isClearable
                    placeholder="Select State"
                    isDisabled={!state.workCity}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>

              {/* Live City */}
              <Col lg={2} md={4} sm={6}>
                <Form.Group className="form-group" data-tour="cmp-livecity">
                  <Form.Label className="col-label">Live City</Form.Label>
                  <Select
                    aria-label="Live City"
                    value={
                      liveCityOptions.find(
                        (option) => option.value === state.liveCity,
                      ) || null
                    }
                    onChange={handleLiveCitySelection}
                    menuPortalTarget={document.body}
                    options={[
                      { value: "", label: "Select City" },
                      ...liveCityOptions,
                    ]}
                    styles={{
                      // Menu + option styling kept in lockstep with the Paycheck
                      // Calculator dropdowns so all calculators feel identical.
                      menu: (base) => ({
                        ...base,
                        background: "var(--mmp-surface)",
                        borderRadius: 10,
                        marginTop: 4,
                        padding: 4,
                        boxShadow:
                          "0 8px 20px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.05)",
                        zIndex: 1000,
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      menuList: (base) => ({
                        ...base,
                        padding: 0,
                        maxHeight: 240,
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: 13,
                        fontWeight: state.isSelected ? 700 : 500,
                        borderRadius: 6,
                        padding: "7px 10px",
                        margin: 0,
                        cursor: "pointer",
                        color: state.isSelected ? "#fff" : "var(--mmp-text-primary)",
                        background: state.isSelected
                          ? "var(--mmp-accent)"
                          : state.isFocused
                          ? "var(--mmp-accent-bg)"
                          : "transparent",
                        // Truncate long option labels with ellipsis so they
                        // can't push the menu wider than the trigger.
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }),
                    }}
                    isClearable
                    placeholder="Select City"
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Info Message */}
            <div className="info-message">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V9H11V15ZM11 7H9V5H11V7Z"
                  fill="var(--mmp-accent)"
                />
              </svg>
              <span>
                Start by selecting your work and living locations to see
                comparison.
              </span>
            </div>
          </Card.Body>
        </Card>

        {/* Comparison Metrics - Outside Card */}
        <Row className="comparison-metrics-row g-3 mt-4">
          {/* Median Gross Salary */}
          <Col lg={3} md={6} sm={6}>
            <div className="metric-card" data-tour="cmp-salary">
              <div className="metric-icon">
                <div className="icon">
                  <GrossSalaryIcon />
                </div>
              </div>
              <div className="metric-content">
                <h6 className="metric-title">
                  Median Gross Salary
                  <InfoTooltip text="Take-home monthly pay for your role in the work city, after taxes." />
                </h6>
                <p className="metric-value">
                  $
                  {remainingCount !== 0 && compareStore?.comparisonItem?.data
                    ? Number(
                        compareStore?.comparisonItem?.data
                          ?.net_monthly_salary_work_city ?? 0,
                      ).toLocaleString("en-US", { maximumFractionDigits: 0 })
                    : "0"}
                  <span className="metric-period">/month</span>
                </p>
                <p className="metric-subtitle">After taxes</p>
              </div>
            </div>
          </Col>

          {/* Average Housing Cost */}
          <Col lg={3} md={6} sm={6}>
            <div className="metric-card" data-tour="cmp-housing">
              <div className="metric-icon">
                <div className="icon">
                  <AvgHousingCostIcon />
                </div>
              </div>
              <div className="metric-content">
                <h6 className="metric-title">
                  Average Housing Cost
                  <InfoTooltip text="Estimated monthly rent in the city you live in." />
                </h6>
                <p className="metric-value">
                  $
                  {remainingCount !== 0 &&
                  compareStore?.comparisonItem?.data
                    ?.monthly_mortgage_of_live_city
                    ? Number(
                        compareStore.comparisonItem.data
                          .monthly_mortgage_of_live_city,
                      ).toLocaleString("en-US", { maximumFractionDigits: 0 })
                    : "0"}
                  <span className="metric-period">/month</span>
                </p>
                <p className="metric-subtitle">Estimated rent</p>
              </div>
            </div>
          </Col>

          {/* Income Taxes */}
          <Col lg={3} md={6} sm={6}>
            <div className="metric-card" data-tour="cmp-taxes">
              <div className="metric-icon">
                <div className="icon">
                  <IncomeTaxIcon />
                </div>
              </div>
              <div className="metric-content">
                <h6 className="metric-title">
                  Income Taxes
                  <InfoTooltip text="Estimated state and federal taxes withheld monthly in the work city." />
                </h6>
                <p className="metric-value">
                  $
                  {remainingCount !== 0 &&
                  compareStore?.comparisonItem?.data
                    ?.taxes_paid_monthly_work_city
                    ? Number(
                        compareStore.comparisonItem.data
                          .taxes_paid_monthly_work_city,
                      ).toLocaleString("en-US", { maximumFractionDigits: 0 })
                    : "0"}
                  <span className="metric-period">/month</span>
                </p>
                <p className="metric-subtitle">State & federal</p>
              </div>
            </div>
          </Col>

          {/* Remaining Income */}
          <Col lg={3} md={6} sm={6}>
            <div className="metric-card highlighted" data-tour="cmp-remaining">
              <div className="metric-icon">
                <div className="icon">
                  <SavingsIcon />
                </div>
              </div>
              <div className="metric-content">
                <h6 className="metric-title">
                  Remaining Income
                  <InfoTooltip text="What's left each month after housing cost and income taxes are subtracted from take-home pay." />
                </h6>
                <p className="metric-value">
                  $
                  {remainingCount !== 0 &&
                  compareStore?.comparisonItem?.data?.original_left_over
                    ? Number(
                        compareStore.comparisonItem.data.original_left_over,
                      ).toLocaleString("en-US", { maximumFractionDigits: 0 })
                    : "0"}
                  <span className="metric-period">/month</span>
                </p>
                <p
                  className={
                    remainingCount !== 0 &&
                    compareStore?.comparisonItem?.data?.original_left_over &&
                    Number(
                      compareStore.comparisonItem.data.original_left_over,
                    ) < 0
                      ? "metric-subtitle-alert"
                      : "metric-subtitle-positive"
                  }
                >
                  {remainingCount !== 0 &&
                  compareStore?.comparisonItem?.data?.original_left_over &&
                  Number(compareStore.comparisonItem.data.original_left_over) <
                    0
                    ? "⚠️ No money left!"
                    : "After housing and taxes"}
                </p>
              </div>
            </div>
          </Col>
        </Row>

        {/* Income vs Expenses Section - In New Card */}
        <Card
          className="comparison-page-box income-expenses-card mt-4"
          data-tour="cmp-ive"
        >
          <Card.Body>
            <div className="income-expenses-section">
              <div className="section-header">
                <h5 className="section-title">
                  Income vs Expenses
                  <InfoTooltip text="Compares your monthly take-home income against total monthly expenses (housing plus taxes) for the selected cities." />
                </h5>
                <p className="section-subtitle">
                  See how your income compares to your total monthly expenses.
                </p>
              </div>

              <div className="chart-container">
                <div className="chart-legend">
                  <div className="legend-item" data-tour="cmp-legend-income">
                    <span className="legend-dot income"></span>
                    <span className="legend-label">Income</span>
                    <span className="legend-value">
                      $
                      {remainingCount !== 0 &&
                      compareStore?.comparisonItem?.data
                        ? Number(
                            compareStore?.comparisonItem?.data
                              ?.net_monthly_salary_work_city ?? 0,
                          ).toLocaleString("en-US", {
                            maximumFractionDigits: 0,
                          })
                        : "0"}
                      /month
                    </span>
                  </div>
                  <div className="legend-item" data-tour="cmp-legend-expenses">
                    <span className="legend-dot expenses"></span>
                    <span className="legend-label">Expenses</span>
                    <span className="legend-value">
                      $
                      {remainingCount !== 0 &&
                      compareStore?.comparisonItem?.data
                        ? (
                            Number(
                              compareStore.comparisonItem.data
                                .monthly_mortgage_of_live_city || 0,
                            ) +
                            Number(
                              compareStore.comparisonItem.data
                                .taxes_paid_monthly_work_city || 0,
                            )
                          ).toLocaleString("en-US", {
                            maximumFractionDigits: 0,
                          })
                        : "0"}
                      /month
                    </span>
                  </div>
                  <div
                    data-tour="cmp-balance"
                    className={
                      remainingCount !== 0 &&
                      compareStore?.comparisonItem?.data?.original_left_over &&
                      Number(
                        compareStore.comparisonItem.data.original_left_over,
                      ) >= 0
                        ? "surplus-value"
                        : "deficit-value"
                    }
                  >
                    {remainingCount !== 0 &&
                    compareStore?.comparisonItem?.data?.original_left_over
                      ? Number(
                          compareStore.comparisonItem.data.original_left_over,
                        ) < 0
                        ? `-$${Math.abs(
                            Number(
                              compareStore.comparisonItem.data
                                .original_left_over,
                            ),
                          ).toLocaleString("en-US", {
                            maximumFractionDigits: 0,
                          })}`
                        : `+$${Number(
                            compareStore.comparisonItem.data.original_left_over,
                          ).toLocaleString("en-US", {
                            maximumFractionDigits: 0,
                          })}`
                      : "$0"}
                    <br />
                    <span className="deficit-label">
                      {remainingCount !== 0 &&
                      compareStore?.comparisonItem?.data?.original_left_over
                        ? Number(
                            compareStore.comparisonItem.data.original_left_over,
                          ) < 0
                          ? "Monthly deficit"
                          : "Monthly surplus"
                        : "Monthly balance"}
                    </span>
                  </div>
                </div>

                <div className="bar-chart">
                  <div className="bar-row" data-tour="cmp-income-bar">
                    <span className="bar-label">Monthly Income</span>
                    <div className="bar-wrapper">
                      <div
                        className="bar income-bar"
                        style={{
                          width:
                            remainingCount !== 0 &&
                            compareStore?.comparisonItem?.data
                              ? (() => {
                                  const income = Number(
                                    compareStore.comparisonItem.data
                                      .net_monthly_salary_work_city || 0,
                                  );
                                  const expenses =
                                    Number(
                                      compareStore.comparisonItem.data
                                        .monthly_mortgage_of_live_city || 0,
                                    ) +
                                    Number(
                                      compareStore.comparisonItem.data
                                        .taxes_paid_monthly_work_city || 0,
                                    );
                                  const maxValue = Math.max(income, expenses);
                                  return maxValue > 0
                                    ? `${(income / maxValue) * 100}%`
                                    : "0%";
                                })()
                              : "0%",
                        }}
                      ></div>
                    </div>
                    <span className="bar-value">
                      $
                      {remainingCount !== 0 &&
                      compareStore?.comparisonItem?.data
                        ? Number(
                            compareStore?.comparisonItem?.data
                              ?.net_monthly_salary_work_city ?? 0,
                          ).toLocaleString("en-US", {
                            maximumFractionDigits: 0,
                          })
                        : "0"}
                    </span>
                  </div>

                  <div className="bar-row" data-tour="cmp-expenses-bar">
                    <span className="bar-label">Total Monthly Expenses</span>
                    <div className="bar-wrapper">
                      <div
                        className="bar expenses-bar"
                        style={{
                          width:
                            remainingCount !== 0 &&
                            compareStore?.comparisonItem?.data
                              ? (() => {
                                  const income = Number(
                                    compareStore.comparisonItem.data
                                      .net_monthly_salary_work_city || 0,
                                  );
                                  const expenses =
                                    Number(
                                      compareStore.comparisonItem.data
                                        .monthly_mortgage_of_live_city || 0,
                                    ) +
                                    Number(
                                      compareStore.comparisonItem.data
                                        .taxes_paid_monthly_work_city || 0,
                                    );
                                  const maxValue = Math.max(income, expenses);
                                  return maxValue > 0
                                    ? `${(expenses / maxValue) * 100}%`
                                    : "0%";
                                })()
                              : "0%",
                        }}
                      ></div>
                    </div>
                    <span className="bar-value">
                      $
                      {remainingCount !== 0 &&
                      compareStore?.comparisonItem?.data
                        ? (
                            Number(
                              compareStore.comparisonItem.data
                                .monthly_mortgage_of_live_city || 0,
                            ) +
                            Number(
                              compareStore.comparisonItem.data
                                .taxes_paid_monthly_work_city || 0,
                            )
                          ).toLocaleString("en-US", {
                            maximumFractionDigits: 0,
                          })
                        : "0"}
                    </span>
                  </div>
                </div>

                <div className="chart-axis">
                  <span>0</span>
                  <span>1k</span>
                  <span>2k</span>
                  <span>3k</span>
                  <span>4k</span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Action Buttons - Outside Card */}
        {/* Removed as per user request */}

        <ComparisonModal
          title={subText?.header}
          description={subText.desc}
          massage1={subText.massage1}
          pluspricem={subText.pluspricem}
          pluspricey={subText.pluspricey}
          prodesc={subText.desc2}
          promassage={subText.promassage}
          propricem={subText.propricem}
          propricey={subText.propricey}
          plan={subText.plan}
          lockedItemName={subText.lockedItemName}
          eventSource={upgradeSource}
          isVisible={subAlertCompModalVisible}
          onClose={() => {
            setSubAlertCompModalVisible(false);
          }}
          onPressYes={() => {
            // Navigate to subscription page or handle upgrade
            navigate("/profile/subscription");
            setSubAlertCompModalVisible(false);
          }}
          yesText="Upgrade Now"
          noText="Cancel"
          // extraData={isRestrictedData}
          extraMetaInfo={true}
        />

        <ActionModal
          title={isRestrictedData.header}
          description={isRestrictedData.comparisanMessage}
          isVisible={subAlertModalVisible}
          eventSource={upgradeSource}
          onClose={() => {
            setSubAlertModalVisible(false);
          }}
          onPressYes={() => {
            // Navigate to subscription page or handle upgrade
            navigate("/profile/subscription", {
              state: { source: upgradeSource },
            });
            setSubAlertModalVisible(false);
          }}
          yesText="Upgrade Now"
          noText="Cancel"
          extraData={isRestrictedData}
          extraMetaInfo={true}
        />

        <CompareDailyLimitModal
          isVisible={compareDailyLimitVisible}
          eventSource={upgradeSource}
          onClose={() => setCompareDailyLimitVisible(false)}
        />
      </div>

      <GuidedTour
        isOpen={isTourOpen}
        steps={comparisonTourSteps}
        onDone={finishTour}
      />
    </>
  );
};

export default Comparison;
